import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatDateValue } from "@/lib/utils";
import { AssistantApiError, AssistantConversation, AssistantMessage, formatAssistantToolActivityLine, walterAssistantClient } from "@/services/walter-assistant";
import { UserType } from "@/types/user";
import { ArrowUp, History, Loader2, SquarePen, ThumbsDown, ThumbsUp } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../ui";
import { StreamingIndicator } from "./streaming-indicator";

type Props = {
    isOpen: boolean;
    user: UserType | null;
};

type ChatStatus = "idle" | "queued" | "streaming" | "completed" | "failed_auth" | "failed_provider" | "not_found" | "bad_request";

function statusFromError(error: AssistantApiError): ChatStatus {
    if (error.status === 400) return "bad_request";
    if (error.status === 401 || error.status === 403) return "failed_auth";
    if (error.status === 404) return "not_found";
    return "failed_provider";
}

function isSameLocalCalendarDay(iso: string, reference: Date = new Date()): boolean {
    const d = new Date(iso);
    return d.getFullYear() === reference.getFullYear() && d.getMonth() === reference.getMonth() && d.getDate() === reference.getDate();
}

function sortConversationsByUpdatedAtDesc(conversations: AssistantConversation[]): AssistantConversation[] {
    return [...conversations].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}

function filterHistoryConversations(conversations: AssistantConversation[]): AssistantConversation[] {
    return conversations.filter(c => c.title?.trim());
}

function normalizeExampleQuestions(conv: Pick<AssistantConversation, "example_questions">): string[] | null {
    const raw = conv.example_questions;
    if (raw == null || !Array.isArray(raw)) return null;
    const filtered = raw.filter((q): q is string => typeof q === "string" && q.trim().length > 0);
    return filtered.length ? filtered : null;
}

const MAX_DISPLAYED_EXAMPLE_QUESTIONS = 5;

const ASSISTANT_MESSAGE_OUTER_CLASS = "flex w-full scroll-mt-16 justify-center";
const ASSISTANT_MESSAGE_INNER_CLASS = "group flex w-full flex-col items-start";
const ASSISTANT_MESSAGE_ARTICLE_CLASS = "text-sm text-gray-800";
const ASSISTANT_MARKDOWN_CLASS = "prose-p:mb-0 prose-p:leading-relaxed";

type AssistantMessageMetaRowProps =
    | { reserveSpaceOnly: true }
    | {
          messageId: number;
          createdAt: string;
          vote: number | null;
          feedbackBusyMessageId: number | null;
          onFeedback: (messageId: number, rating: 1 | -1) => void;
      };

function AssistantMessageMetaRow(props: AssistantMessageMetaRowProps) {
    if ("reserveSpaceOnly" in props) {
        return (
            <div className="pointer-events-none flex w-full min-w-0 items-center justify-start" aria-hidden="true">
                <Button size="icon" variant="ghost" tabIndex={-1} className="invisible p-0 text-gray-400">
                    <ThumbsUp className="size-4" />
                </Button>
                <Button size="icon" variant="ghost" tabIndex={-1} className="invisible p-0 text-gray-400">
                    <ThumbsDown className="size-4" />
                </Button>
                <div className="ml-1 text-[10px] text-gray-500 opacity-0">00:00</div>
            </div>
        );
    }

    const { messageId, createdAt, vote, feedbackBusyMessageId, onFeedback } = props;
    if (messageId <= 0) {
        return <AssistantMessageMetaRow reserveSpaceOnly />;
    }

    return (
        <div className="flex w-full min-w-0 items-center justify-start">
            <Button
                size="icon"
                aria-label="Réponse utile"
                title="Réponse utile"
                disabled={feedbackBusyMessageId !== null}
                onClick={() => onFeedback(messageId, 1)}
                variant="ghost"
                className={cn(vote === 1 ? "p-0 text-emerald-700 hover:text-emerald-700" : "p-0 text-gray-400 hover:bg-gray-50 hover:text-gray-600")}
            >
                <ThumbsUp className="size-4" />
            </Button>
            <Button
                size="icon"
                aria-label="Réponse peu utile"
                title="Réponse peu utile"
                disabled={feedbackBusyMessageId !== null}
                onClick={() => onFeedback(messageId, -1)}
                variant="ghost"
                className={cn(vote === -1 ? "p-0 text-red-700 hover:text-red-700" : "p-0 text-gray-400 hover:bg-gray-50 hover:text-gray-600")}
            >
                <ThumbsDown className="size-4" />
            </Button>
            <div className="ml-1 text-[10px] text-gray-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                {new Date(createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit"
                })}
            </div>
        </div>
    );
}

function pickAtMostExampleQuestions(questions: string[]): string[] {
    if (questions.length <= MAX_DISPLAYED_EXAMPLE_QUESTIONS) return questions;
    const picked = new Set<number>();
    while (picked.size < MAX_DISPLAYED_EXAMPLE_QUESTIONS) {
        picked.add(Math.floor(Math.random() * questions.length));
    }
    return Array.from(picked, i => questions[i]);
}

function buildSessionContext(pathname: string, user: UserType | null): Record<string, unknown> {
    const segments = pathname.split("/").filter(Boolean);
    const objectName = segments[0] || null;
    const objectId = segments[1] || null;
    const isNumericId = objectId ? /^\d+$/.test(objectId) : false;
    const context: Record<string, unknown> = {
        crm_user_profile: user?.profile ?? null,
        sales_process: user?.sf_user?.sales_process ?? null,
        crm_path: pathname
    };

    if (objectName && isNumericId) {
        context.crm_object_name = objectName;
        context.crm_object_id = objectId;

        if (objectName.includes("account")) {
            context.account_id = objectId;
        }
        if (objectName.includes("contact") || objectName.includes("lead")) {
            context.contact_id = objectId;
        }
    }

    return context;
}

export function WalterIaChatPanel({ isOpen, user }: Props) {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [status, setStatus] = useState<ChatStatus>("idle");
    const [errorText, setErrorText] = useState<string | null>(null);
    const [conversations, setConversations] = useState<AssistantConversation[]>([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [selectedConversationId, setSelectedConversationId] = useState<string>("");
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [exampleQuestions, setExampleQuestions] = useState<string[] | null>(null);
    const [draft, setDraft] = useState("");
    const [streamedAssistantText, setStreamedAssistantText] = useState("");
    const [streamingActivityLabel, setStreamingActivityLabel] = useState<string | null>(null);
    const [feedbackBusyMessageId, setFeedbackBusyMessageId] = useState<number | null>(null);
    const [spacerHeight, setSpacerHeight] = useState(0);
    const [anchorMode, setAnchorMode] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const scrollRootRef = useRef<HTMLDivElement | null>(null);
    const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
    const turnEndRef = useRef<HTMLDivElement | null>(null);
    const shouldAnchorTopRef = useRef(false);
    const isInitializedRef = useRef(false);
    const lastPatchedContextRef = useRef<string>("");

    const getViewport = useCallback(() => scrollRootRef.current?.querySelector<HTMLDivElement>("[data-radix-scroll-area-viewport]") ?? null, []);

    const sessionContext = useMemo(() => buildSessionContext(location.pathname, user), [location.pathname, user]);

    const orderedMessages = useMemo(() => {
        return [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }, [messages]);

    const lastUserMessageId = useMemo(() => {
        for (let i = orderedMessages.length - 1; i >= 0; i--) {
            if (orderedMessages[i].role === "user") return orderedMessages[i].id;
        }
        return null;
    }, [orderedMessages]);

    const messageGroups = useMemo(() => {
        const result: Array<[string, AssistantMessage[]]> = [];
        let lastKey: string | null = null;
        for (const m of orderedMessages) {
            const key = new Date(m.created_at).toDateString();
            if (key !== lastKey) {
                result.push([key, [m]]);
                lastKey = key;
            } else {
                result[result.length - 1][1].push(m);
            }
        }
        return result;
    }, [orderedMessages]);

    useEffect(() => {
        if (!isOpen) {
            isInitializedRef.current = false;
            lastPatchedContextRef.current = "";
        }
    }, [isOpen]);

    // Réinitialise l'état chat quand l'utilisateur CRM change sans fermer le
    // panneau (sinon on garde l'UUID d'une conversation d'un autre compte → 404
    // sur `/send`).
    useEffect(() => {
        isInitializedRef.current = false;
        lastPatchedContextRef.current = "";
        setConversations([]);
        setSelectedConversationId("");
        setMessages([]);
        setExampleQuestions(null);
        setDraft("");
        setStreamedAssistantText("");
        setStreamingActivityLabel(null);
        setStatus("idle");
        setErrorText(null);
        setHistoryOpen(false);
    }, [user?.id]);

    useEffect(() => {
        if (!isOpen || isLoading || !selectedConversationId) return;
        const frameId = requestAnimationFrame(() => {
            textareaRef.current?.focus();
        });
        return () => cancelAnimationFrame(frameId);
    }, [isOpen, isLoading, selectedConversationId]);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [draft]);

    // Reset de l'ancrage quand on change/recharge de conversation.
    useEffect(() => {
        setAnchorMode(false);
        setSpacerHeight(0);
        shouldAnchorTopRef.current = false;
    }, [selectedConversationId]);

    // Hors mode ancrage (chargement initial, reload), on colle au bas pour voir
    // le dernier message.
    useEffect(() => {
        if (isLoading || anchorMode) return;
        const viewport = getViewport();
        if (viewport) viewport.scrollTop = viewport.scrollHeight;
    }, [isLoading, selectedConversationId, anchorMode, getViewport]);

    // Mode ancrage : la bulle utilisateur est épinglée en haut du panel et la
    // réponse se déroule en dessous. On dimensionne un spacer pour que le tour
    // courant (question + réponse) occupe au moins toute la hauteur visible, ce
    // qui permet de remonter la question tout en haut sans gap superflu.
    useLayoutEffect(() => {
        if (!anchorMode) return;
        const viewport = getViewport();
        const userEl = lastUserMessageRef.current;
        const endEl = turnEndRef.current;
        if (!viewport || !userEl || !endEl) return;

        const turnHeight = endEl.getBoundingClientRect().top - userEl.getBoundingClientRect().top;
        setSpacerHeight(Math.max(0, viewport.clientHeight - turnHeight));

        if (shouldAnchorTopRef.current) {
            shouldAnchorTopRef.current = false;
            requestAnimationFrame(() => {
                lastUserMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }, [anchorMode, orderedMessages, streamedAssistantText, streamingActivityLabel, getViewport]);

    useEffect(() => {
        if (!isOpen || !user?.walter_assistant_profile || isInitializedRef.current) return;

        const initialize = async () => {
            setIsLoading(true);
            setErrorText(null);
            try {
                await walterAssistantClient.checkAuth();
                const rawList = await walterAssistantClient.listConversations({ agentProfile: "sales_assistant" });
                const sorted = sortConversationsByUpdatedAtDesc(rawList);
                const latest = sorted[0];
                const useLatestToday = Boolean(latest && isSameLocalCalendarDay(latest.updated_at));

                if (useLatestToday && latest) {
                    setConversations(sorted);
                    setSelectedConversationId(latest.id);
                    setExampleQuestions(normalizeExampleQuestions(latest));
                    setMessages(await walterAssistantClient.getMessages(latest.id));
                } else {
                    const createdConversation = await walterAssistantClient.createConversation({
                        sourceApp: "walter_crm",
                        sessionContext
                    });
                    setConversations([createdConversation, ...sorted]);
                    setSelectedConversationId(createdConversation.id);
                    setExampleQuestions(normalizeExampleQuestions(createdConversation));
                    setMessages([]);
                }
                isInitializedRef.current = true;
            } catch (error) {
                const message = error instanceof Error ? error.message : "Unable to initialize Walter IA";
                setErrorText(message);
            } finally {
                setIsLoading(false);
            }
        };

        void initialize();
    }, [isOpen, sessionContext, user?.walter_assistant_profile]);

    useEffect(() => {
        if (!isOpen || !selectedConversationId) return;

        const contextAsString = JSON.stringify(sessionContext);
        if (lastPatchedContextRef.current === contextAsString) return;

        lastPatchedContextRef.current = contextAsString;
        void walterAssistantClient.patchConversationSessionContext(selectedConversationId, sessionContext).catch(() => {
            // Ignore silent context patch failures to avoid disturbing chat flow.
        });
    }, [isOpen, selectedConversationId, sessionContext]);

    const createConversation = async () => {
        setIsLoading(true);
        setErrorText(null);
        try {
            const created = await walterAssistantClient.createConversation({
                sourceApp: "walter_crm",
                sessionContext
            });
            setConversations(previous => [created, ...previous]);
            setSelectedConversationId(created.id);
            setExampleQuestions(normalizeExampleQuestions(created));
            setMessages([]);
            setStatus("idle");
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unable to create conversation";
            setErrorText(message);
        } finally {
            setIsLoading(false);
        }
    };

    const selectConversation = async (conv: AssistantConversation) => {
        if (conv.id === selectedConversationId) return;
        setIsLoading(true);
        setErrorText(null);
        setStatus("idle");
        try {
            const msgs = await walterAssistantClient.getMessages(conv.id);
            setSelectedConversationId(conv.id);
            setExampleQuestions(normalizeExampleQuestions(conv));
            setMessages(msgs);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unable to load conversation";
            setErrorText(message);
        } finally {
            setIsLoading(false);
        }
    };

    const refreshConversationList = async () => {
        try {
            const rawList = await walterAssistantClient.listConversations({ agentProfile: "sales_assistant" });
            setConversations(sortConversationsByUpdatedAtDesc(rawList));
        } catch {
            // Silently ignore list refresh failures.
        }
    };

    const sendUserContent = async (rawContent: string, clearDraft: boolean = true) => {
        const content = rawContent.trim();
        if (!selectedConversationId || !content || isSending) return;
        const optimisticId = -Date.now();
        let pendingAssistantText = "";
        let rafId: number | null = null;

        setStatus("queued");
        setIsSending(true);
        setErrorText(null);
        if (clearDraft) setDraft("");
        setStreamedAssistantText("");
        setStreamingActivityLabel(null);
        setMessages(previous => [
            ...previous,
            {
                id: optimisticId,
                role: "user",
                content,
                created_at: new Date().toISOString()
            }
        ]);
        shouldAnchorTopRef.current = true;
        setAnchorMode(true);

        const flush = () => {
            if (!pendingAssistantText) return;
            const chunk = pendingAssistantText;
            pendingAssistantText = "";
            setStreamedAssistantText(previous => previous + chunk);
        };

        try {
            const sendResponse = await walterAssistantClient.sendMessage(selectedConversationId, content, sessionContext);
            setStatus("streaming");
            await walterAssistantClient.streamConversation(selectedConversationId, sendResponse.message_id, event => {
                if (event.eventType === "response_start") {
                    setStreamingActivityLabel("Préparation de la réponse…");
                    return;
                }

                if (event.eventType === "tool_activity" && event.data && typeof event.data === "object" && event.data !== null) {
                    setStreamingActivityLabel(formatAssistantToolActivityLine(event.data as Record<string, unknown>));
                    return;
                }

                if (event.eventType === "response_delta" && event.data && typeof event.data === "object" && "text" in event.data) {
                    const eventData = event.data as Record<string, unknown>;
                    pendingAssistantText += String(eventData.text ?? "");
                    if (rafId === null) {
                        rafId = window.requestAnimationFrame(() => {
                            flush();
                            rafId = null;
                        });
                    }
                    return;
                }

                if (event.eventType === "response_end") {
                    if (rafId !== null) {
                        window.cancelAnimationFrame(rafId);
                        rafId = null;
                    }
                    flush();
                    setStreamingActivityLabel(null);
                    setStatus("completed");
                }

                if (event.eventType === "error") {
                    if (rafId !== null) {
                        window.cancelAnimationFrame(rafId);
                        rafId = null;
                    }
                    flush();
                    setStreamingActivityLabel(null);
                    setStatus("failed_provider");
                }
            });

            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            flush();
            const persistedMessages = await walterAssistantClient.getMessages(selectedConversationId);
            setMessages(persistedMessages);
            setStreamedAssistantText("");
            setStreamingActivityLabel(null);
        } catch (error) {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
                rafId = null;
            }
            setStreamedAssistantText("");
            setStreamingActivityLabel(null);
            setMessages(previous => previous.filter(message => message.id !== optimisticId));
            if (error instanceof AssistantApiError) {
                setStatus(statusFromError(error));
                setErrorText(typeof error.data === "object" && error.data && "detail" in error.data ? String(error.data.detail) : error.message);
            } else {
                setStatus("failed_provider");
                setErrorText(error instanceof Error ? error.message : "Unable to send message");
            }
        } finally {
            setIsSending(false);
        }
    };

    const sendMessage = async () => {
        await sendUserContent(draft, true);
    };

    const submitAssistantFeedback = async (messageId: number, rating: 1 | -1) => {
        if (!selectedConversationId || feedbackBusyMessageId !== null) return;
        setFeedbackBusyMessageId(messageId);
        const snapshot = messages.find(m => m.id === messageId);
        const prevRating = snapshot?.feedback_rating ?? null;
        const optimisticRating = prevRating === rating ? null : rating;
        setMessages(current => current.map(m => (m.id === messageId ? { ...m, feedback_rating: optimisticRating } : m)));
        try {
            const rep = await walterAssistantClient.submitAssistantMessageFeedback(selectedConversationId, messageId, rating);
            setMessages(current => current.map(m => (m.id === messageId ? { ...m, feedback_rating: rep.feedback_rating } : m)));
        } catch {
            setMessages(current => current.map(m => (m.id === messageId ? { ...m, feedback_rating: prevRating } : m)));
            toast.error("Impossible d'enregistrer votre avis.");
        } finally {
            setFeedbackBusyMessageId(null);
        }
    };

    const displayedExampleQuestions = useMemo(() => {
        if (!exampleQuestions?.length) return [];
        return pickAtMostExampleQuestions(exampleQuestions);
    }, [exampleQuestions, selectedConversationId]);

    const isComposingNew = !isLoading && messages.length === 0 && !streamedAssistantText && !isSending;
    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        const greetings = hour >= 5 && hour < 19 ? ["Bonjour", "Bonjour", "Bonjour", "Hola"] : ["Bonsoir", "Bonsoir", "Bonsoir", "Hola"];
        return greetings[Math.floor(Math.random() * greetings.length)];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversationId]);
    const showExamplePrompts = isComposingNew && displayedExampleQuestions.length > 0;
    const waitingForAssistantStream = isSending && !streamedAssistantText.trim() && (status === "queued" || status === "streaming");

    if (!user?.walter_assistant_profile) {
        return <div className="flex h-full items-center justify-center text-sm text-gray-500">Walter IA is not enabled for this user.</div>;
    }

    return (
        <div className="relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white">
            <ScrollArea ref={scrollRootRef} className="min-h-0 flex-1">
                <div className="flex min-h-full flex-col px-4 pb-24 pt-4">
                    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
                        {isLoading && (
                            <div className="flex h-40 flex-1 items-center justify-center">
                                <Loader2 className="size-6 animate-spin text-gray-600" />
                            </div>
                        )}

                        {!isLoading && (
                            <>
                                {messageGroups.map(([day, dayMessages], groupIndex) => {
                                    const isLastGroup = groupIndex === messageGroups.length - 1;
                                    return (
                                        <div key={day} className="flex flex-col gap-3">
                                            <div className="relative my-3 flex items-center justify-center">
                                                <span className="absolute rounded-full bg-white px-2 text-[10px] font-medium text-gray-400">{formatDateValue(new Date(day), "long")}</span>
                                            </div>
                                            {dayMessages.map(message => {
                                                const isUser = message.role === "user";
                                                const optimisticUser = isUser && message.id < 0;
                                                const isAssistant = message.role === "assistant";
                                                const vote = message.feedback_rating ?? null;
                                                return (
                                                    <div
                                                        key={message.id}
                                                        ref={message.id === lastUserMessageId ? lastUserMessageRef : undefined}
                                                        className={cn(isUser ? "flex w-full scroll-mt-16 justify-end" : ASSISTANT_MESSAGE_OUTER_CLASS)}
                                                    >
                                                        <div className={cn(isUser ? "group flex w-full max-w-md flex-col items-end" : ASSISTANT_MESSAGE_INNER_CLASS)}>
                                                            <article
                                                                className={cn(
                                                                    isUser ? "rounded-2xl bg-off-white px-3 py-2 text-sm text-black " : ASSISTANT_MESSAGE_ARTICLE_CLASS,
                                                                    optimisticUser && "duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
                                                                )}
                                                            >
                                                                {isUser ? (
                                                                    <div className="whitespace-pre-wrap break-words">{message.content}</div>
                                                                ) : (
                                                                    <MarkdownRenderer content={message.content} className={ASSISTANT_MARKDOWN_CLASS} />
                                                                )}
                                                            </article>
                                                            <div className={cn("flex w-full min-w-0 items-center ", isUser ? "justify-end" : "justify-start")}>
                                                                {isAssistant ? (
                                                                    <AssistantMessageMetaRow
                                                                        messageId={message.id}
                                                                        createdAt={message.created_at}
                                                                        vote={vote}
                                                                        feedbackBusyMessageId={feedbackBusyMessageId}
                                                                        onFeedback={submitAssistantFeedback}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className={cn("ml-1 text-[10px] text-gray-500 transition-opacity duration-300", "mr-2 mt-1 opacity-0 group-hover:opacity-100")}
                                                                    >
                                                                        {new Date(message.created_at).toLocaleTimeString([], {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit"
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {isLastGroup && waitingForAssistantStream && (
                                                <div className="flex w-full items-start" role="status" aria-live="polite">
                                                    <span className="sr-only">{streamingActivityLabel ? `Walter prépare une réponse. ${streamingActivityLabel}` : "Walter prépare une réponse…"}</span>
                                                    <StreamingIndicator label={streamingActivityLabel} />
                                                </div>
                                            )}

                                            {isLastGroup && streamedAssistantText && (
                                                <div className={ASSISTANT_MESSAGE_OUTER_CLASS}>
                                                    <div className={ASSISTANT_MESSAGE_INNER_CLASS}>
                                                        <article className={ASSISTANT_MESSAGE_ARTICLE_CLASS}>
                                                            <MarkdownRenderer content={streamedAssistantText} className={ASSISTANT_MARKDOWN_CLASS} />
                                                        </article>
                                                        <AssistantMessageMetaRow reserveSpaceOnly />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        <div ref={turnEndRef} />
                        <div aria-hidden style={{ height: spacerHeight }} />
                    </div>
                </div>
            </ScrollArea>

            {/* Questions d'exemple affichées sous la textarea centrée */}
            <div
                className={cn(
                    "absolute inset-x-0 z-10 flex   justify-center px-3 transition-[top,opacity] duration-500 ease-out",
                    isComposingNew && showExamplePrompts ? "top-[calc(50%+48px)] opacity-100" : "pointer-events-none top-[calc(50%+64px)] opacity-0"
                )}
            >
                <ul className="flex w-full max-w-2xl flex-wrap items-center justify-center gap-2">
                    {displayedExampleQuestions.map((question, index) => (
                        <li key={`${index}-${question}`}>
                            <button
                                type="button"
                                disabled={isSending}
                                className={cn(
                                    "w-fit rounded-full border border-gray-200 bg-gray-50 px-2 py-1.5 text-left text-xs text-gray-800 shadow-sm transition-colors",
                                    "hover:border-gray-300 hover:bg-white disabled:pointer-events-none disabled:opacity-50"
                                )}
                                onClick={() => void sendUserContent(question, false)}
                            >
                                {question}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Salutation affichée au-dessus de la textarea centrée */}
            <div
                className={cn(
                    "absolute inset-x-0 z-10 flex justify-center px-3 transition-[bottom,opacity] duration-500 ease-out",
                    isComposingNew ? "bottom-[calc(50%+56px)] opacity-100" : "pointer-events-none bottom-[calc(50%+72px)] opacity-0"
                )}
            >
                <p className="text-2xl font-semibold text-gray-800">
                    {greeting}, {user?.first_name}
                </p>
            </div>

            {/* Barre de saisie : centrée en nouvelle conversation, dockée en bas sinon */}
            <div
                className={cn(
                    "absolute inset-x-0 z-10 px-3 pb-3 pt-2 transition-[bottom,transform,backdrop-filter] duration-500 ease-out",
                    isComposingNew ? "bottom-1/2 translate-y-1/2" : "bottom-0 translate-y-0 bg-white/60 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/60"
                )}
            >
                <div className={cn("mx-auto w-full transition-[max-width] duration-500 ease-out", isComposingNew ? "max-w-lg" : "max-w-3xl")}>
                    {errorText && (
                        <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
                            <span className="min-w-0 truncate">{errorText}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setErrorText(null);
                                    toast.info("Réessayez en renvoyant votre message.");
                                }}
                                className="shrink-0 rounded bg-red-100 px-2 py-0.5 font-medium hover:bg-red-200"
                            >
                                Réessayer
                            </button>
                        </div>
                    )}
                    <form
                        className="flex items-center gap-2"
                        onSubmit={e => {
                            e.preventDefault();
                            void sendMessage();
                        }}
                    >
                        <Popover
                            open={historyOpen}
                            onOpenChange={open => {
                                setHistoryOpen(open);
                                if (open) void refreshConversationList();
                            }}
                        >
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" title="Historique" disabled={isLoading || isSending} className="h-9 w-9 rounded-full border border-gray-200  hover:bg-gray-50">
                                    <History className="size-4 shrink-0" aria-hidden />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent side="top" align="start" className="w-72 p-0">
                                <div className="max-h-72 overflow-y-auto">
                                    {filterHistoryConversations(conversations).length === 0 ? (
                                        <p className="px-3 py-4 text-center text-xs text-gray-400">Aucune conversation</p>
                                    ) : (
                                        <ul className="py-1">
                                            {sortConversationsByUpdatedAtDesc(filterHistoryConversations(conversations)).map(conv => (
                                                <li key={conv.id}>
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-gray-50",
                                                            conv.id === selectedConversationId && "bg-gray-100"
                                                        )}
                                                        onClick={() => {
                                                            setHistoryOpen(false);
                                                            void selectConversation(conv);
                                                        }}
                                                    >
                                                        <span className="line-clamp-1 text-sm font-medium text-gray-800">{conv.title?.trim() || "Nouvelle conversation"}</span>
                                                        <span className="text-[10px] text-gray-400">
                                                            {new Date(conv.updated_at).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}
                                                        </span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                        {!isComposingNew && (
                            <Button
                                variant="outline"
                                size="sm"
                                title="Nouvelle conversation"
                                onClick={() => void createConversation()}
                                disabled={isLoading || isSending}
                                className="h-9 w-9 rounded-full"
                            >
                                <SquarePen className="size-3.5 shrink-0" aria-hidden />
                            </Button>
                        )}
                        <div className="flex w-full items-end gap-1 rounded-[20px] border border-gray-200 bg-white py-0.5 pl-4 pr-[5px] ">
                            <Textarea
                                ref={textareaRef}
                                className="scrollbar-hide max-h-40 min-h-8 flex-1 resize-none border-0 p-0 py-1.5 shadow-none focus-visible:ring-0"
                                placeholder={selectedConversationId ? "Message à Walter IA…" : "Créez une conversation d’abord"}
                                value={draft}
                                disabled={!selectedConversationId || isSending}
                                onChange={event => {
                                    setDraft(event.target.value);
                                }}
                                onKeyDown={event => {
                                    if (event.key !== "Enter" || event.shiftKey) return;
                                    if (event.nativeEvent.isComposing) return;
                                    event.preventDefault();
                                    void sendMessage();
                                }}
                                rows={1}
                            />
                            <button
                                type="submit"
                                disabled={!selectedConversationId || !draft.trim() || isSending}
                                className={cn(
                                    "mb-0.5 flex aspect-square h-7 w-7 shrink-0 items-center justify-center self-end rounded-full transition-colors",
                                    !selectedConversationId || !draft.trim() || isSending
                                        ? "pointer-events-none cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "bg-mastic-grey text-black hover:bg-mastic-grey/90"
                                )}
                            >
                                {isSending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
